import { useMemo } from 'react';
import useLevel from './useLevel'; // Importamos el hook de nivel que ya creamos

export default function useDailyQuests(userProgress) {
  // Necesitamos saber el nivel del usuario para ajustar la dificultad
  const lifetimeXP = userProgress?.lifetimeXp || userProgress?.xp || 0;
  const { level } = useLevel(lifetimeXP);

  const questsData = useMemo(() => {
    // 1. Obtenemos los stats de hoy (si no ha jugado hoy, todo es 0)
    const todayString = new Date().toDateString();
    const todayStats = userProgress?.dailyStats?.date === todayString 
      ? userProgress.dailyStats 
      : { xp: 0, sets: 0, perfectSets: 0, minutes: 0 };

    let quests = [];

    // === MISIÓN 1: Calentamiento (Para TODOS) ===
    // Meta muy fácil para garantizar retención.
    const q1Target = level < 10 ? 1 : 2; // Si es novato, 1 set. Si es veterano, 2 sets.
    quests.push({
      id: 'quest_1',
      title: 'Calentamiento',
      description: `Completa ${q1Target} set${q1Target > 1 ? 's' : ''} de ejercicios.`,
      icon: '🎯',
      current: todayStats.sets,
      target: q1Target,
      rewardXP: 20,
      isCompleted: todayStats.sets >= q1Target
    });

    // === MISIÓN 2: Hábito (Nivel 11 en adelante) ===
    // Aumentamos el compromiso con tiempo o XP bruta.
    if (level >= 11) {
      const q2Target = level < 25 ? 100 : 250; // XP a ganar hoy
      quests.push({
        id: 'quest_2',
        title: 'Energía Diaria',
        description: `Gana ${q2Target} XP el día de hoy.`,
        icon: '⚡',
        current: todayStats.xp,
        target: q2Target,
        rewardXP: 50,
        isCompleted: todayStats.xp >= q2Target
      });
    }

    // === MISIÓN 3: Maestría (Nivel 26 en adelante) ===
    // Un reto para los hardcore (Sets perfectos o mucho tiempo)
    if (level >= 26) {
      // Determinamos el reto de forma semi-aleatoria basada en el día para variar
      // Usamos el día del mes (1 al 31) para alternar entre reto de Precisión o Tiempo
      const dayOfMonth = new Date().getDate();
      const isAccuracyDay = dayOfMonth % 2 === 0;

      if (isAccuracyDay) {
        quests.push({
          id: 'quest_3',
          title: 'Perfeccionista',
          description: 'Completa 1 set sin cometer errores.',
          icon: '✨',
          current: todayStats.perfectSets,
          target: 1,
          rewardXP: 100,
          isCompleted: todayStats.perfectSets >= 1
        });
      } else {
        const timeTarget = 20; // 20 minutos
        quests.push({
          id: 'quest_3',
          title: 'Dedicación',
          description: `Estudia por al menos ${timeTarget} minutos.`,
          icon: '⏳',
          current: todayStats.minutes,
          target: timeTarget,
          rewardXP: 100,
          isCompleted: todayStats.minutes >= timeTarget
        });
      }
    }

    // 2. Calcular el progreso global del día
    const completedQuests = quests.filter(q => q.isCompleted).length;
    const totalQuests = quests.length;
    const allCompleted = completedQuests === totalQuests;
    const progressPercentage = (completedQuests / totalQuests) * 100;

    return {
      quests,               // El array de misiones para pintar en pantalla
      completedQuests,      // Cuántas lleva
      totalQuests,          // Cuántas tiene asignadas hoy (1, 2 o 3)
      allCompleted,         // Booleano por si quieres darle un premio extra al completar todo
      progressPercentage    // Para la barra circular de tu diseño
    };

  }, [userProgress, level]);

  return questsData;
}